import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Slider
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Speed as SpeedIcon,
  DataUsage as DataUsageIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { Line, Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  RadialLinearScale,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import dataQualityService from '../../services/dataQualityService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  RadialLinearScale,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

const QualityDetails = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [timeRange, setTimeRange] = useState('30d');
  const [qualityThreshold, setQualityThreshold] = useState(70);
  const [showTrends, setShowTrends] = useState(true);
  const [expandedPanel, setExpandedPanel] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [qualityData, setQualityData] = useState({
    overallScore: 0,
    trendDirection: 'stable',
    trendPercentage: 0,
    dataVolume: 0,
    lastUpdated: new Date().toISOString(),
    dimensions: {}
  });

  const [historicalTrends, setHistoricalTrends] = useState({ labels: [], datasets: [] });

  const fetchQualityDetails = useCallback(async () => {
    setLoading(true);
    try {
      const [metrics, dimensions, trends] = await Promise.all([
        dataQualityService.getDashboardMetrics(),
        dataQualityService.getQualityDimensions(),
        dataQualityService.getQualityTrends({ timeRange })
      ]);

      const avgScore = parseFloat(metrics?.avgScore ?? metrics?.avg_score ?? 0);
      const totalAssessments = parseInt(metrics?.totalAssessments ?? metrics?.total_assessments ?? 0);

      const dimensionMap = {};
      const dimNames = ['temporal', 'geographical', 'technological', 'reliability', 'completeness'];
      const dimTargets = { temporal: 80, geographical: 85, technological: 75, reliability: 80, completeness: 90 };
      const dimDescriptions = {
        temporal: 'How recently the data was collected relative to the reporting period',
        geographical: 'How well the data represents the specific geographic region',
        technological: 'How well the data represents the specific technology used',
        reliability: 'The quality and reliability of the data source',
        completeness: 'The percentage of required data fields that are populated'
      };

      // dimensions endpoint returns array of {entityType, count, avgScore, dimensions:{...}}
      // We need to aggregate across entity types or use a specific one
      const dimArray = Array.isArray(dimensions) ? dimensions : (dimensions?.dimensions || []);

      dimNames.forEach(dim => {
        let score = 0;
        if (dimArray.length > 0) {
          // Average the dimension score across all entity types
          const scores = dimArray.map(d => d.dimensions?.[dim] || 0).filter(s => s > 0);
          score = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
        }
        // Also check the dashboard dimensions format
        if (score === 0 && metrics.dimensions) {
          const dimKey = dim === 'completeness' ? 'completeness' : dim;
          score = metrics.dimensions[dimKey] || 0;
        }
        const target = dimTargets[dim];
        dimensionMap[dim] = {
          current: score,
          target,
          trend: score >= target ? 'increasing' : score >= target * 0.8 ? 'stable' : 'decreasing',
          trendValue: Math.round((score - target) * 10) / 10,
          issues: 0,
          description: dimDescriptions[dim]
        };
      });

      setQualityData({
        overallScore: Math.round(avgScore * 10) / 10,
        trendDirection: avgScore >= 70 ? 'increasing' : 'stable',
        trendPercentage: Math.round((avgScore - 65) * 10) / 10,
        dataVolume: totalAssessments,
        lastUpdated: new Date().toISOString(),
        dimensions: dimensionMap
      });

      if (trends && trends.length > 0) {
        setHistoricalTrends({
          labels: trends.map(t => new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
          datasets: dimNames.map((dim, i) => {
            const colors = [
              theme.palette.success.main,
              theme.palette.info.main,
              theme.palette.warning.main,
              theme.palette.error.main,
              theme.palette.secondary.main
            ];
            return {
              label: dim.charAt(0).toUpperCase() + dim.slice(1),
              data: trends.map(t => {
                const camelKey = 'avg' + dim.charAt(0).toUpperCase() + dim.slice(1);
                return Math.round(parseFloat(t[camelKey] || t[`avg_${dim}`] || t.avgScore || t.avg_score || 0));
              }),
              borderColor: colors[i],
              backgroundColor: colors[i] + '20',
              fill: true,
              tension: 0.4
            };
          })
        });
      }
    } catch (err) {
      console.error('Error fetching quality details:', err);
      setSnackbar({ open: true, message: `Failed to load quality details: ${err.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [timeRange, theme]);

  useEffect(() => {
    fetchQualityDetails();
  }, [fetchQualityDetails]);

  const getQualityColor = (score, target) => {
    if (score >= target) return theme.palette.success.main;
    if (score >= target * 0.8) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const getQualityIcon = (score, target) => {
    if (score >= target) return <CheckCircleIcon />;
    if (score >= target * 0.8) return <WarningIcon />;
    return <ErrorIcon />;
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'increasing': return <TrendingUpIcon color="success" />;
      case 'decreasing': return <TrendingDownIcon color="error" />;
      case 'stable': return <SpeedIcon color="info" />;
      default: return <SpeedIcon />;
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { usePointStyle: true, padding: 20 } },
      title: { display: false }
    },
    scales: {
      y: { beginAtZero: true, max: 100, ticks: { callback: (v) => v + '%' } },
      x: { grid: { display: false } }
    },
    interaction: { intersect: false, mode: 'index' },
    elements: { point: { radius: 4, hoverRadius: 8 } }
  };

  const radarData = {
    labels: Object.keys(qualityData.dimensions).map(k => k.charAt(0).toUpperCase() + k.slice(1)),
    datasets: [
      {
        label: 'Current Score',
        data: Object.values(qualityData.dimensions).map(d => d.current),
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.main + '20',
        pointBackgroundColor: theme.palette.primary.main
      },
      {
        label: 'Target Score',
        data: Object.values(qualityData.dimensions).map(d => d.target),
        borderColor: theme.palette.success.main,
        backgroundColor: theme.palette.success.main + '20',
        pointBackgroundColor: theme.palette.success.main
      }
    ]
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom' } },
    scales: { r: { beginAtZero: true, max: 100, ticks: { callback: (v) => v + '%' } } }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>Loading quality details...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">Quality Details</Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchQualityDetails} disabled={loading}>Refresh</Button>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Time Range</InputLabel>
          <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} label="Time Range">
            <MenuItem value="7d">Last 7 days</MenuItem>
            <MenuItem value="30d">Last 30 days</MenuItem>
            <MenuItem value="90d">Last 90 days</MenuItem>
            <MenuItem value="365d">Last year</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Switch checked={showTrends} onChange={(e) => setShowTrends(e.target.checked)} size="small" />}
          label="Show Trends"
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
          <Typography variant="body2">Quality Threshold:</Typography>
          <Box sx={{ width: 120 }}>
            <Slider value={qualityThreshold} onChange={(e, v) => setQualityThreshold(v)} valueLabelDisplay="auto" min={0} max={100} size="small" />
          </Box>
        </Box>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h2" color="primary.main">{qualityData.overallScore}%</Typography>
                <Typography variant="h6" color="text.secondary">Overall Quality Score</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {getTrendIcon(qualityData.trendDirection)}
                <Typography variant="body1" sx={{ ml: 1 }}>
                  {qualityData.trendDirection === 'increasing' ? 'Improving' : qualityData.trendDirection === 'decreasing' ? 'Declining' : 'Stable'}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Total Assessments: {qualityData.dataVolume.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Last Updated: {new Date(qualityData.lastUpdated).toLocaleString()}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <DataUsageIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">PACT DQI Scoring</Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Quality Dimensions</Typography>
            <Grid container spacing={2}>
              {Object.entries(qualityData.dimensions).map(([key, dimension]) => (
                <Grid item xs={12} md={6} key={key}>
                  <Card
                    sx={{
                      cursor: 'pointer', '&:hover': { boxShadow: 4 },
                      border: dimension.current < dimension.target ? `1px solid ${theme.palette.warning.main}` : 'none'
                    }}
                    onClick={() => { setSelectedMetric({ key, ...dimension }); setDialogOpen(true); }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>{key}</Typography>
                        {getQualityIcon(dimension.current, dimension.target)}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h4" color={getQualityColor(dimension.current, dimension.target)}>{dimension.current}%</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>/ {dimension.target}%</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        {getTrendIcon(dimension.trend)}
                        <Typography variant="body2" sx={{ ml: 1 }}>{dimension.trendValue > 0 ? '+' : ''}{dimension.trendValue}%</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={dimension.current}
                        sx={{
                          height: 8, borderRadius: 4,
                          backgroundColor: theme.palette.grey[200],
                          '& .MuiLinearProgress-bar': { backgroundColor: getQualityColor(dimension.current, dimension.target), borderRadius: 4 }
                        }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {dimension.issues} entities below threshold
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Quality Radar</Typography>
            <Box sx={{ height: 300, mt: 2 }}>
              {Object.keys(qualityData.dimensions).length > 0 ? (
                <Radar data={radarData} options={radarOptions} />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', pt: 10 }}>No dimension data</Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {showTrends && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Historical Trends</Typography>
          <Box sx={{ height: 300, mt: 2 }}>
            {historicalTrends.labels.length > 0 ? (
              <Line data={historicalTrends} options={chartOptions} />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', pt: 10 }}>
                No trend data available for selected range
              </Typography>
            )}
          </Box>
        </Paper>
      )}

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Detailed Metrics</Typography>
        {Object.entries(qualityData.dimensions).map(([key, dimension]) => (
          <Accordion key={key} expanded={expandedPanel === key} onChange={(e, isExpanded) => setExpandedPanel(isExpanded ? key : false)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Typography variant="h6" sx={{ textTransform: 'capitalize', mr: 2 }}>{key}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>{dimension.current}%</Typography>
                <Box sx={{ flexGrow: 1, mx: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={dimension.current}
                    sx={{
                      height: 6, borderRadius: 3,
                      backgroundColor: theme.palette.grey[200],
                      '& .MuiLinearProgress-bar': { backgroundColor: getQualityColor(dimension.current, dimension.target), borderRadius: 3 }
                    }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">{dimension.issues} issues</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{dimension.description}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Current</Typography>
                  <Typography variant="h6" color={getQualityColor(dimension.current, dimension.target)}>{dimension.current}%</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Target</Typography>
                  <Typography variant="h6" color="success.main">{dimension.target}%</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Gap</Typography>
                  <Typography variant="h6" color={dimension.current >= dimension.target ? 'success.main' : 'error.main'}>
                    {dimension.current >= dimension.target ? '+' : ''}{dimension.current - dimension.target}%
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Trend</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>{getTrendIcon(dimension.trend)}</Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>

      <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); setSelectedMetric(null); }} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AssignmentIcon sx={{ mr: 1 }} />
            {selectedMetric?.key && selectedMetric.key.charAt(0).toUpperCase() + selectedMetric.key.slice(1)} Details
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedMetric && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">Current Score</Typography>
                  <Typography variant="h4" color={getQualityColor(selectedMetric.current, selectedMetric.target)}>{selectedMetric.current}%</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">Target Score</Typography>
                  <Typography variant="h4" color="success.main">{selectedMetric.target}%</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">Entities Below Threshold</Typography>
                  <Typography variant="h5" color="error.main">{selectedMetric.issues}</Typography>
                </Grid>
              </Grid>
              <Typography variant="body1">{selectedMetric.description}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogOpen(false); setSelectedMetric(null); }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default QualityDetails;
